import type { AccessTokenFunction, OwletEventHandlers } from "../types";
import type {
  IActivateMessage,
  IMessage,
  INotificationMessage,
} from "./messageTypes/Message";
import { handleMessage } from "./incomingMessageHandler";
import type { AdaptiveLogger } from "@1js/office-online-jsapi-core";
import type { IWACPageNotification } from "./messageTypes/Notification";

export let messagePort: MessagePort | null = null;

export function initPostMessaging(
  frame: HTMLIFrameElement,
  frameOrigin: string,
  contentWindow: Window,
  channelId: string,
  getAuthToken: AccessTokenFunction,
  eventHandlers: OwletEventHandlers,
  logger?: AdaptiveLogger
): void {
  let intervalId: number = -1;
  messagePort = null;
  let frameLoaded = false;

  // Listens for the initialize message from doc.aspx
  function messageListener(event: MessageEvent): void {
    if (event.source && contentWindow && event.source === contentWindow) {
      const message = event.data;
      if (message.type === "initialize" && message.channelId === channelId) {
        const port = event.ports[0];
        messagePort = port;
        initMessageHandler(port, getAuthToken, eventHandlers, logger);
        // Clean up event listener and interval once port is established
        if (intervalId !== -1) {
          clearInterval(intervalId);
          intervalId = -1;
        }
        window.removeEventListener("message", messageListener);
      }
    }
  }

  // Waiting for response from doc.aspx iframe
  window.addEventListener("message", messageListener);

  // Wait for iframe to load before identifying as parent
  frame.addEventListener("load", () => (frameLoaded = true));
  // open port for postMessage communication once iframe contentWindow is defined
  // try to post message every 500ms until doc.aspx acknowledges with 'initialize'
  if (!messagePort && contentWindow) {
    intervalId = window.setInterval(() => {
      if (frameLoaded) {
        contentWindow.postMessage(
          { type: "identify-parent", channelId },
          frameOrigin
        );
        frame.removeEventListener("load", () => (frameLoaded = true));
      }
    }, 500);
  }
}

function initMessageHandler(
  messagePort: MessagePort,
  getAuthToken: AccessTokenFunction,
  eventHandlers: OwletEventHandlers,
  logger?: AdaptiveLogger
) {
  // Sets up handler for all messages from doc.aspx
  async function messageListener(event: MessageEvent): Promise<void> {
    await handleMessage(event.data, eventHandlers, getAuthToken, logger);
  }

  if (messagePort) {
    messagePort.addEventListener("message", messageListener);
    messagePort.start();
    const activateMessage: IActivateMessage = {
      type: "activate",
    };
    sendMessage(activateMessage);
  }
}

export function sendMessage(message: IMessage): void {
  if (!messagePort) {
    console.error("Message port not set");
    return;
  }
  messagePort.postMessage(message);
}

export function sendMessageAwaitResponse<
  //  Currently only extends IWACPageNotification
  TResponseNotificationType extends IWACPageNotification,
  TResult,
>(
  message: IMessage,
  responseMessageId: string,
  messageHandler: (event: TResponseNotificationType) => TResult
): Promise<TResult> {
  return new Promise((resolve, reject) => {
    if (!messagePort) {
      console.error("Message port not set");
      reject();
    }
    const listener = (event: MessageEvent) => {
      // TODO: extend this function to work with other message types (i.e. command, result)
      const response = event?.data as INotificationMessage;
      if (response?.data?.notification !== responseMessageId) {
        return;
      }
      try {
        messagePort!.removeEventListener("message", listener);
        const result = messageHandler(
          response.data as TResponseNotificationType
        );
        resolve(result);
      } catch (error) {
        reject(error);
      }
    };
    messagePort!.addEventListener("message", listener);
    sendMessage(message);
  });
}
